export const getTableContextForCell = (editor, cell) => {
  if (!editor || !cell || !editor.contains(cell)) return null;
  const row = cell.closest('tr');
  const table = cell.closest('table');
  if (!row || !table) return null;
  return { cell, row, table, section: row.parentElement };
};

export const createEmptyTableCell = (tagName = 'td') => {
  const cell = document.createElement(tagName);
  cell.innerHTML = '<br>';
  return cell;
};

export const insertRowAtCell = (context, after) => {
  const newRow = document.createElement('tr');
  const columns = Array.from(context.row.cells).reduce((count, cell) => count + (cell.colSpan || 1), 0);
  const tagName = context.row.cells[0]?.tagName?.toLowerCase() === 'th' ? 'th' : 'td';
  for (let index = 0; index < Math.max(columns, 1); index += 1) {
    newRow.appendChild(createEmptyTableCell(tagName));
  }
  context.section.insertBefore(newRow, after ? context.row.nextSibling : context.row);
  return newRow.cells[Math.min(context.cell.cellIndex, newRow.cells.length - 1)];
};

export const insertColumnAtCell = (context, after) => {
  const targetIndex = context.cell.cellIndex + (after ? 1 : 0);
  Array.from(context.table.rows).forEach((row) => {
    const reference = row.cells[targetIndex] || null;
    const neighbor = row.cells[Math.max(0, targetIndex - (after ? 1 : 0))];
    const tagName = neighbor?.tagName?.toLowerCase() === 'th' ? 'th' : 'td';
    row.insertBefore(createEmptyTableCell(tagName), reference);
  });
  return context.table.rows[context.row.rowIndex]?.cells[targetIndex] || null;
};

export const moveTableRow = (context, direction) => {
  const sibling = direction === 'up' ? context.row.previousElementSibling : context.row.nextElementSibling;
  if (!sibling) return null;
  if (direction === 'up') context.section.insertBefore(context.row, sibling);
  else context.section.insertBefore(sibling, context.row);
  return context.row.cells[Math.min(context.cell.cellIndex, context.row.cells.length - 1)] || null;
};

export const getTableRowAtDragPoint = (table, target, clientY) => {
  if (!table) return null;
  const directRow = target?.closest?.('tr') || null;
  if (directRow && directRow.closest('table') === table) return directRow;
  if (!Number.isFinite(clientY)) return null;

  return Array.from(table.rows).find((row) => {
    const rect = row.getBoundingClientRect();
    return clientY >= rect.top && clientY <= rect.bottom;
  }) || null;
};

export const getTableHandleLayout = (container, cell, pointerX = null) => {
  const row = cell?.closest('tr');
  const table = cell?.closest('table');
  if (!container || !cell || !row || !table) return null;

  const containerRect = container.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();

  return {
    cell,
    rowTop: rowRect.top - containerRect.top,
    rowBottom: rowRect.bottom - containerRect.top,
    rowCenter: rowRect.top + (rowRect.height / 2) - containerRect.top,
    rowLeft: tableRect.left - containerRect.left,
    columnTop: tableRect.top - containerRect.top,
    columnLeft: cellRect.left - containerRect.left,
    columnRight: cellRect.right - containerRect.left,
    columnCenter: cellRect.left + (cellRect.width / 2) - containerRect.left,
    canReorderRow: Boolean(row.parentElement?.rows?.length > 1),
    showColumnHandles: row === table.rows[0],
    showColumnResizeHandle: row === table.rows[0]
      && cell.tagName === 'TH'
      && pointerX !== null
      && Math.abs(pointerX - cellRect.right) <= 10
  };
};

const replaceCellTag = (cell, tagName) => {
  if (!cell || cell.tagName.toLowerCase() === tagName) return cell;
  const replacement = document.createElement(tagName);
  Array.from(cell.attributes).forEach((attribute) => {
    replacement.setAttribute(attribute.name, attribute.value);
  });
  while (cell.firstChild) replacement.appendChild(cell.firstChild);
  cell.replaceWith(replacement);
  return replacement;
};

export const mergeTableCellSelection = (context, cells) => {
  if (!context || cells.length < 2) return null;
  const rows = cells.map((cell) => cell.parentElement.rowIndex);
  const columns = cells.map((cell) => cell.cellIndex);
  const minRow = Math.min(...rows);
  const maxRow = Math.max(...rows);
  const minColumn = Math.min(...columns);
  const maxColumn = Math.max(...columns);
  const rectangularCells = [];

  for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
    for (let columnIndex = minColumn; columnIndex <= maxColumn; columnIndex += 1) {
      const cell = context.table.rows[rowIndex]?.cells[columnIndex];
      if (cell) rectangularCells.push(cell);
    }
  }
  if (rectangularCells.length < 2) return null;

  const target = rectangularCells[0];
  const contents = rectangularCells.slice(1)
    .map((cell) => cell.innerHTML)
    .filter((html) => html && html !== '<br>');
  if (contents.length) {
    target.innerHTML += `${target.textContent.trim() ? '<br>' : ''}${contents.join('<br>')}`;
  }
  target.rowSpan = maxRow - minRow + 1;
  target.colSpan = maxColumn - minColumn + 1;
  rectangularCells.slice(1).forEach((cell) => cell.remove());
  return target;
};

export const splitTableCellAtContext = (context) => {
  if (!context) return null;
  const rowSpan = context.cell.rowSpan || 1;
  const colSpan = context.cell.colSpan || 1;
  if (rowSpan === 1 && colSpan === 1) return null;

  const rowIndex = context.row.rowIndex;
  const cellIndex = context.cell.cellIndex;
  context.cell.rowSpan = 1;
  context.cell.colSpan = 1;
  for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
    const row = context.table.rows[rowIndex + rowOffset];
    if (!row) continue;
    const additions = colSpan - (rowOffset === 0 ? 1 : 0);
    for (let columnOffset = 0; columnOffset < additions; columnOffset += 1) {
      const referenceIndex = cellIndex + (rowOffset === 0 ? 1 : 0);
      row.insertBefore(createEmptyTableCell(context.cell.tagName.toLowerCase()), row.cells[referenceIndex] || null);
    }
  }
  return context.cell;
};

export const toggleTableHeader = (context, axis) => {
  if (!context?.table.rows.length) return null;
  const attribute = axis === 'row' ? 'data-header-row' : 'data-header-column';
  const cells = axis === 'row'
    ? Array.from(context.table.rows[0].cells)
    : Array.from(context.table.rows).map((row) => row.cells[0]).filter(Boolean);
  const currentlyHeader = context.table.getAttribute(attribute) === 'true'
    || cells.every((cell) => cell.tagName === 'TH');
  const makeHeader = !currentlyHeader;
  context.table.setAttribute(attribute, String(makeHeader));

  cells.forEach((cell, index) => {
    const intersectsOtherHeader = axis === 'row'
      ? index === 0 && context.table.getAttribute('data-header-column') === 'true'
      : index === 0 && context.table.getAttribute('data-header-row') === 'true';
    replaceCellTag(cell, makeHeader || intersectsOtherHeader ? 'th' : 'td');
  });

  return axis === 'row'
    ? context.table.rows[0]?.cells[Math.min(context.cell.cellIndex, context.table.rows[0].cells.length - 1)]
    : context.table.rows[context.row.rowIndex]?.cells[0];
};
